import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getMyTeam } from "../api";

const PENDING_JOIN_CODE_KEY = "reviewflow_pending_join_code";
// Item 20: where ProtectedRoute bounced a logged-out visitor from, so /login
// can restore it after the GitHub round-trip instead of always landing on
// /dashboard. A plain sessionStorage key, not GitHub's `state` param — that's
// already reserved for invite codes (item 15) and reusing it here would
// collide with that mechanism.
const PENDING_REDIRECT_KEY = "reviewflow_pending_redirect";

const AuthContext = createContext(null);

/**
 * Session state for the whole app.
 *
 * Extracted from App.js when routing was introduced: the marketing nav needs
 * to know whether someone is signed in (to swap "Start free trial" for
 * "Dashboard →"), and the app routes need it to gate access. Keeping it in
 * App.js would have meant prop-drilling through the router.
 *
 * GitHub is the identity provider, not the session mechanism — this reads our
 * own JWT from localStorage, never a GitHub token. See CLAUDE.md.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [hasTeam, setHasTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingJoinCode, setPendingJoinCode] = useState(null);
  const [joinError, setJoinError] = useState(null);
  // Item 20: set once from the auth redirect's new_trial/trial_ends_at
  // params, only when auth-callback's resolveTeamForInstallation created a
  // brand-new team. Never re-derived after mount, so it naturally fires
  // exactly once per real trial start — the URL params are gone (replaced)
  // by the time any later render could re-read them.
  const [justStartedTrial, setJustStartedTrial] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // The session payload comes back in a URL fragment, not a query param —
    // fragments are never sent to servers, so the token stays out of Referer
    // headers and access logs. Errors stay in the query string; they're not
    // sensitive and are easier to read there.
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));

    // A ?join=CODE deep link won't survive the GitHub OAuth round-trip as a
    // query param, so persist it before any redirect happens. /join/:code
    // writes to the same key, so both entry styles converge here.
    const joinCodeFromUrl = params.get("join");
    if (joinCodeFromUrl) {
      sessionStorage.setItem(PENDING_JOIN_CODE_KEY, joinCodeFromUrl);
    }
    setPendingJoinCode(sessionStorage.getItem(PENDING_JOIN_CODE_KEY));

    const authData = hashParams.get("auth");
    const authError = params.get("auth_error");
    // A join-by-state failure isn't an auth failure (the user is genuinely
    // signed in) — it rides alongside the #auth= fragment as query params
    // rather than through the fragment-less auth_error path (item 15).
    const joinErrorCode = params.get("join_error");
    // Item 20: same non-error, alongside-the-fragment convention as
    // join_error above — a one-time signal, not a failure state.
    const newTrialFlag = params.get("new_trial");
    const trialEndsAtParam = params.get("trial_ends_at");

    if (authData) {
      try {
        const decoded = JSON.parse(atob(decodeURIComponent(authData)));
        if (decoded.user) {
          setUser(decoded.user);
          localStorage.setItem("reviewflow_user", JSON.stringify(decoded.user));
          if (decoded.session_token) {
            localStorage.setItem("session_token", decoded.session_token);
          }
          const hasExistingTeam = !!decoded.user.team_id;
          setHasTeam(hasExistingTeam);
          if (hasExistingTeam) {
            sessionStorage.removeItem(PENDING_JOIN_CODE_KEY);
          }
        }
      } catch (err) {
        console.error("Failed to parse auth data:", err);
      }
      if (joinErrorCode) {
        setJoinError({ code: joinErrorCode, teamName: params.get("team_name") || null });
      }
      // Defence in depth. auth-callback is responsible for sending new_trial
      // only when a trial was actually granted — since org-scoped trials, a
      // brand-new team does NOT always get one, and a null end date would
      // serialise to 1970-01-01. A truthiness check passes that happily, and
      // the banner would announce a trial that ended in 1969. Requiring a date
      // in the future means no backend mistake can produce that false claim.
      const trialEndsAtMs = trialEndsAtParam ? Date.parse(trialEndsAtParam) : NaN;
      const isNewTrial =
        newTrialFlag === "1" && Number.isFinite(trialEndsAtMs) && trialEndsAtMs > Date.now();
      if (isNewTrial) {
        setJustStartedTrial({ endsAt: trialEndsAtParam });
      }
      // Land on the app rather than the marketing page, and drop the fragment
      // so the token doesn't sit in the address bar or browser history entry.
      // Item 20: restore wherever ProtectedRoute originally bounced this
      // visitor from (/login stashed it before the GitHub redirect), rather
      // than always dumping them on /dashboard regardless of what they were
      // actually trying to reach. Read-once-then-clear, same as
      // PENDING_JOIN_CODE_KEY's staleness handling above. A genuine new-trial
      // signup always wins and goes to /dashboard regardless of any stashed
      // destination — otherwise a stale PENDING_REDIRECT_KEY left over from
      // an abandoned /login attempt earlier in the same tab could silently
      // strand a brand-new signup on some other page, never showing the
      // trial banner (which only renders on Dashboard).
      const pendingRedirect = sessionStorage.getItem(PENDING_REDIRECT_KEY);
      sessionStorage.removeItem(PENDING_REDIRECT_KEY);
      // ⚠️ A failed join goes to /onboarding, and this is not cosmetic.
      // JOIN_ERROR_MESSAGES lives in Onboarding — it is the join surface, with
      // the code field and the retry. /dashboard renders TeamlessPanel, which
      // has no idea joinError exists, so landing there after a failed join
      // showed the user NOTHING: no reason, no retry, just the generic
      // teamless screen. That regressed the moment /dashboard stopped
      // branching on joinError, and it is exactly the class of silent
      // dead-end this whole change set out to remove.
      //
      // Still one fact-based rule, evaluated fresh — a server-reported join
      // failure, not a stored preference.
      const destination = joinErrorCode
        ? "/onboarding"
        : isNewTrial
          ? "/dashboard"
          : (pendingRedirect || "/dashboard");
      navigate(destination, { replace: true });
      setLoading(false);
    } else if (authError) {
      console.error("Auth error:", authError);
      navigate("/", { replace: true });
      setLoading(false);
    } else {
      const savedUser = localStorage.getItem("reviewflow_user");
      const sessionToken = localStorage.getItem("session_token");
      if (savedUser && sessionToken) {
        try {
          const parsed = JSON.parse(savedUser);
          setUser(parsed);
          getMyTeam()
            .then((res) => {
              const team = res.data?.data?.team;
              setHasTeam(!!team);
              if (team) {
                parsed.team_id = team.team_id;
                setUser({ ...parsed });
                localStorage.setItem("reviewflow_user", JSON.stringify(parsed));
                sessionStorage.removeItem(PENDING_JOIN_CODE_KEY);
              }
            })
            .catch(() => {
              setHasTeam(!!parsed.team_id);
            })
            .finally(() => setLoading(false));
        } catch (e) {
          localStorage.removeItem("reviewflow_user");
          setLoading(false);
        }
      } else {
        setLoading(false);
      }

      // Legacy root-level ?join=CODE deep link — consolidate onto the one
      // canonical entry point (/join/:code) that knows how to thread the
      // code through OAuth's `state` param. Safe to fire regardless of the
      // session-restore branch above: JoinPage waits on `loading` itself
      // before deciding whether to bounce an already-authenticated visitor
      // further to /dashboard.
      if (joinCodeFromUrl) {
        navigate(`/join/${encodeURIComponent(joinCodeFromUrl)}`, { replace: true });
      }
    }
    // Intentionally runs once on mount, as it did in App.js.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // `redirectTo` (item 24): account deletion needs to land on
  // /login?reason=deleted rather than the marketing homepage — dropping a
  // just-deleted user on "/" with no acknowledgement is the same dead end
  // item 20 fixed for expired sessions. Optional and defaulted, so every
  // existing `onLogout` handler (which passes nothing, or is wired straight
  // to an onClick and would otherwise pass a click event) is unaffected.
  //
  // `hard` exists because a router navigate LOSES this redirect: clearing
  // `user` re-renders ProtectedRoute, which immediately issues its own
  // <Navigate to="/login"> and clobbers ours, stripping the query string —
  // so the user lands on a bare login page with no explanation. A full page
  // load sidesteps the race entirely, matching what api.js's 401 interceptor
  // already does for the same reason, and guarantees no React state survives
  // from an account that no longer exists.
  const logout = useCallback((redirectTo, { hard = false } = {}) => {
    setUser(null);
    setHasTeam(null);
    setJoinError(null);
    localStorage.removeItem("reviewflow_user");
    localStorage.removeItem("session_token");
    // A pending invite code is a one-shot intent belonging to the person who
    // followed the link, and sessionStorage survives logout — so without this
    // it leaks into the next session in this tab. It also used to change which
    // screen /dashboard rendered, which is how a stale code could send someone
    // to the wizard for no visible reason (gotcha #54).
    sessionStorage.removeItem(PENDING_JOIN_CODE_KEY);
    setPendingJoinCode(null);
    const target = typeof redirectTo === "string" ? redirectTo : "/";
    if (hard) {
      window.location.href = target;
      return;
    }
    navigate(target, { replace: true });
  }, [navigate]);

  // Called after a successful leaveTeam() API response. Clears team_id,
  // writes the fresh (team_id: null) JWT so any endpoint trusting the JWT
  // claim is correct immediately rather than up to 24h later, and routes
  // back to /dashboard where the teamless user sees Onboarding again.
  const leaveCurrentTeam = useCallback((sessionToken) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, team_id: null };
      localStorage.setItem("reviewflow_user", JSON.stringify(updated));
      return updated;
    });
    if (sessionToken) {
      localStorage.setItem("session_token", sessionToken);
    }
    setHasTeam(false);
    navigate("/dashboard", { replace: true });
  }, [navigate]);

  // Called after a successful deleteTeam(). Same session bookkeeping as
  // leaveCurrentTeam — clear team_id, adopt the fresh JWT, land on /dashboard
  // — with one difference: it also switches the teamless screen to
  // TeamlessHome.
  //
  // Deliberately NOT folded into leaveCurrentTeam with a flag. Leaving a team
  // you could rejoin and destroying one permanently are different intentions,
  // and the screens should differ: someone who left a team by choice may well
  // want the install flow next, while someone who just deleted theirs has
  // explicitly finished with it.
  const deleteCurrentTeam = useCallback((sessionToken) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, team_id: null };
      localStorage.setItem("reviewflow_user", JSON.stringify(updated));
      return updated;
    });
    if (sessionToken) {
      localStorage.setItem("session_token", sessionToken);
    }
    setHasTeam(false);
    navigate("/dashboard", { replace: true });
  }, [navigate]);

  // The exact inverse of deleteCurrentTeam, for a successful restoreTeam().
  //
  // ⚠️ hasTeam must flip back to true here. Dashboard tests `hasTeam === false`
  // and AppHome tests `!hasTeam` (gotcha #55 — they are deliberately different,
  // because rendering and connecting fail safe in opposite directions), so
  // leaving it false after a restore would keep showing the teamless screens
  // for a team that is demonstrably back. Reloading instead of navigating for
  // the same reason the delete path does not: every surface caches its own
  // getMyTeam() result, and a restore changes all of them at once.
  const restoreCurrentTeam = useCallback((sessionToken, teamId) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, team_id: teamId };
      localStorage.setItem("reviewflow_user", JSON.stringify(updated));
      return updated;
    });
    if (sessionToken) {
      localStorage.setItem("session_token", sessionToken);
    }
    setHasTeam(true);
  }, []);

  // Item 22: merges a successful PATCH /api/users/me response into local
  // state, the same shape leaveCurrentTeam uses for team_id. No fresh JWT
  // needed here — display_name/contact_email were never JWT claims (only
  // user_id/github_username/team_id are), so there's nothing to re-sign.
  const updateProfileFields = useCallback((fields) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, ...fields };
      localStorage.setItem("reviewflow_user", JSON.stringify(updated));
      return updated;
    });
  }, []);

  const rememberJoinCode = useCallback((code) => {
    if (!code) return;
    sessionStorage.setItem(PENDING_JOIN_CODE_KEY, code);
    setPendingJoinCode(code);
  }, []);

  // Item 20: called by Login.js when ProtectedRoute handed it a `from`
  // location, right before redirecting to GitHub — mirrors rememberJoinCode's
  // pattern exactly, just a different sessionStorage key.
  const rememberRedirectDestination = useCallback((path) => {
    if (!path) return;
    sessionStorage.setItem(PENDING_REDIRECT_KEY, path);
  }, []);

  const value = {
    user,
    hasTeam,
    loading,
    pendingJoinCode,
    joinError,
    justStartedTrial,
    isAuthenticated: !!user,
    logout,
    leaveCurrentTeam,
    deleteCurrentTeam,
    restoreCurrentTeam,
    updateProfileFields,
    rememberJoinCode,
    rememberRedirectDestination,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
